import { fromBase64URL, toBase64URL } from "../bytes";
import { HermesError } from "../errors";

/**
 * 经 TimiBot 中转连接一台主机所需的信息（来自主机上 connector 打印的配对链接）。
 *
 *   timibot://pair?r=<中转地址>&h=<host_id>&k=<主机公钥 base64url>&c=<一次性配对码>&n=<名字>[&u=<用户>&p=<密码>][&g=<Bot>&b=<Bot 显示名>]
 */
export interface RelayRoute {
  relayURL: string;
  hostID: string;
  /** 主机（connector）的 X25519 静态公钥，base64url */
  hostKey: string;
  name: string;
  /** 这条连接用哪把手机密钥（= 主机眼里的哪台设备）。省略表示按主机 id 存的那把。 */
  keyID?: string;
}

export function keyAccount(route: RelayRoute): string {
  return route.keyID ?? route.hostID;
}

export function sameRoute(a: RelayRoute | undefined, b: RelayRoute | undefined): boolean {
  if (!a || !b) return a === b;
  return a.relayURL === b.relayURL && a.hostID === b.hostID && a.hostKey === b.hostKey && a.name === b.name && a.keyID === b.keyID;
}

/** 手机连中转的 WebSocket 地址：wss://relay/v1/hosts/<id>/client */
export function clientURL(route: RelayRoute): string {
  const u = new URL(route.relayURL);
  u.protocol = u.protocol === "http:" ? "ws:" : "wss:";
  const prefix = u.pathname.endsWith("/") ? u.pathname.slice(0, -1) : u.pathname;
  u.pathname = `${prefix}/v1/hosts/${route.hostID}/client`;
  u.search = "";
  u.hash = "";
  return u.toString();
}

export function hostKeyBytes(route: RelayRoute): Uint8Array {
  const k = fromBase64URL(route.hostKey);
  if (!k || k.length !== 32) throw HermesError.invalidConfiguration("配对信息里的主机公钥无效，请重新配对");
  return k;
}

/** 配对链接：主机上 `pair` 打印的一次性链接，或者主人分享 Bot 生成的邀请链接（多 g、b 两个参数）。 */
export interface RelayPairing {
  route: RelayRoute;
  code?: string;
  username?: string;
  password?: string;
  /** 邀请链接分享的 Bot（Hermes profile 名）；省略 = 主人配对 */
  sharedBot?: string;
  botTitle?: string;
}

export function isInvite(p: RelayPairing): boolean {
  return p.sharedBot !== undefined;
}

export function parsePairingLink(raw: string): RelayPairing | undefined {
  const text = raw.trim();
  if (!/^timibot:\/\/pair\?/i.test(text)) return undefined;
  const query = text.slice(text.indexOf("?") + 1);
  const q = new Map<string, string>();
  for (const part of query.split("&")) {
    if (!part) continue;
    const eq = part.indexOf("=");
    // 和 iOS 的 URLComponents 一样：只做百分号解码，“+” 原样保留
    const k = safeDecode(eq < 0 ? part : part.slice(0, eq));
    const v = eq < 0 ? "" : safeDecode(part.slice(eq + 1));
    if (!q.has(k)) q.set(k, v);
  }
  const relay = q.get("r");
  const host = q.get("h");
  const key = q.get("k");
  if (!relay || !host || !key) return undefined;
  let relayURL: URL;
  try { relayURL = new URL(relay); } catch { return undefined; }
  if (relayURL.protocol !== "https:" && relayURL.protocol !== "http:") return undefined;
  const keyBytes = fromBase64URL(key);
  if (!keyBytes || keyBytes.length !== 32) return undefined;
  const nonEmpty = (v: string | undefined) => (v === undefined || v === "" ? undefined : v);
  return {
    route: { relayURL: relay, hostID: host, hostKey: toBase64URL(keyBytes), name: nonEmpty(q.get("n")) ?? "我的 Hermes" },
    code: q.get("c"),
    username: q.get("u"),
    password: q.get("p"),
    sharedBot: nonEmpty(q.get("g")),
    botTitle: nonEmpty(q.get("b")),
  };
}

function safeDecode(text: string): string {
  try { return decodeURIComponent(text); } catch { return text; }
}

/** 8 位短码（例如 K7QM-3XPA）：主机配对码和分享 Bot 的邀请码是同一套。 */
export const RelayInviteCode = {
  /** 输入里去掉横杠空格、转大写后是 8 位（不含 0/O/1/I）就当作邀请码 */
  normalize(raw: string): string | undefined {
    const code = raw.toUpperCase().replace(/[^A-Z0-9]/g, "");
    return /^[2-9A-HJ-NP-Z]{8}$/.test(code) ? code : undefined;
  },

  display(code: string): string {
    return code.length === 8 ? `${code.slice(0, 4)}-${code.slice(4)}` : code;
  },

  /** 向中转查询邀请码对应的链接。 */
  async resolve(code: string, relay: string, fetchImpl: typeof fetch = fetch): Promise<RelayPairing> {
    const base = relay.endsWith("/") ? relay : relay + "/";
    let res: Response;
    try {
      res = await fetchImpl(new URL(`v1/invites/${code}`, base).toString(), { signal: AbortSignal.timeout(15000) });
    } catch (e) {
      throw HermesError.transport(`连不上 TimiBot 中转服务：${e instanceof Error ? e.message : String(e)}`);
    }
    if (res.status === 404) throw HermesError.invalidConfiguration("配对码不存在或已过期");
    if (res.status === 429) throw HermesError.invalidConfiguration("尝试太频繁，请稍后再试");
    let link: string | undefined;
    try { link = (await res.json())?.link; } catch { /* 下面报错 */ }
    const pairing = res.status === 200 && typeof link === "string" ? parsePairingLink(link) : undefined;
    if (!pairing) throw HermesError.invalidConfiguration(`配对码无效（HTTP ${res.status}）`);
    return pairing;
  },
};

/** connector 握手时告诉手机的信息。 */
export interface RelayWelcome {
  name?: string;
  /** "none"：connector 替手机鉴权，直接连；"login"：Hermes 开了门禁，需要用 Hermes 账号登录 */
  auth?: string;
  hermes?: string;
  connector?: string;
  backend_error?: string;
  /** connector 当前使用的中转地址 */
  relay?: string;
  /** "owner" 或 "guest"；老 connector 没有 = 主人 */
  role?: string;
  profiles?: string[];
}

export function parseWelcome(raw: unknown): RelayWelcome {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : undefined);
  return {
    name: str(o.name),
    auth: str(o.auth),
    hermes: str(o.hermes),
    connector: str(o.connector),
    backend_error: str(o.backend_error),
    relay: str(o.relay),
    role: str(o.role),
    profiles: Array.isArray(o.profiles) ? o.profiles.filter((x): x is string => typeof x === "string") : undefined,
  };
}

export const Welcome = {
  requiresLogin: (w: RelayWelcome | undefined) => w?.auth === "login",
  isGuest: (w: RelayWelcome | undefined) => w?.role === "guest",
  /** connector 1.1 起支持分享 Bot */
  supportsSharing: (w: RelayWelcome | undefined) => connectorAtLeast(w, "1.1"),
  /** 1.2 起：Key 跟随主账号、共用语音 Key */
  supportsFollow: (w: RelayWelcome | undefined) => connectorAtLeast(w, "1.2"),
  supportsPush: (w: RelayWelcome | undefined) => connectorAtLeast(w, "1.3"),
};

export function connectorAtLeast(w: RelayWelcome | undefined, version: string): boolean {
  if (!w?.connector) return false;
  return compareVersions(w.connector, version) >= 0;
}

export function compareVersions(a: string, b: string): number {
  const pa = a.split(/[^0-9]+/).filter(Boolean).map(Number);
  const pb = b.split(/[^0-9]+/).filter(Boolean).map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}
